import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import "./Home.css";

export default function Home() {
  const [products, setProducts] = useState(null);
  const [banners, setBanners] = useState([]);
  const [offers, setOffers] = useState([]);
  const [search, setSearch] = useState("");
  const [bannerIdx, setBannerIdx] = useState(0);
  const bannerRef = useRef(null);
  const offerRef = useRef(null);

  useEffect(() => {
    api.get("/product/all").then((r) => setProducts(r.data.products)).catch(() => setProducts([]));
    api.get("/product/banners").then((r) => setBanners(r.data.banners || [])).catch(() => {});
    api.get("/product/offers").then((r) => setOffers(r.data.offers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setBannerIdx((i) => (i + 1) % banners.length), 4000);
    return () => clearInterval(t);
  }, [banners.length]);

  const filtered = (products || []).filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.company || "").toLowerCase().includes(search.toLowerCase())
  );

  const groups = filtered.reduce((acc, p) => {
    (acc[p.category || "Games"] = acc[p.category || "Games"] || []).push(p);
    return acc;
  }, {});

  function renderStars(rating) {
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    let stars = "";
    for (let i = 0; i < full; i++) stars += "★";
    if (half) stars += "★";
    for (let i = stars.length; i < 5; i++) stars += "☆";
    return stars;
  }

  return (
    <div className="home">
      {banners.length > 0 && (
        <div className="home-carousel">
          <div className="home-carousel-track" ref={bannerRef}
            style={{ transform: `translateX(-${bannerIdx * 100}%)` }}>
            {banners.map((b, i) => (
              <div key={i} className="home-carousel-slide">
                {/\.(mp4|webm)$/i.test(b.image) ? (
                  <video src={`/${b.image}`} autoPlay muted loop playsInline />
                ) : (
                  <img src={`/${b.image}`} alt="" />
                )}
              </div>
            ))}
          </div>
          {banners.length > 1 && (
            <div className="home-dots">
              {banners.map((_, i) => (
                <button key={i} className={`home-dot${i === bannerIdx ? " on" : ""}`}
                  onClick={() => setBannerIdx(i)} aria-label={`Slide ${i + 1}`} />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="home-search-wrap">
        <div className="home-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input type="text" placeholder="Search games..." value={search}
            onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Flash Sale / Offers */}
      {offers.length > 0 && (
        <section className="home-section">
          <div className="sec-banner">
            <div className="sec-title">🔥 Flash Sale <span className="sec-count">{offers.length}</span></div>
            <div className="sec-sub">Limited-time deals — grab them before they're gone!</div>
          </div>
          <div className="offer-scroll" ref={offerRef}>
            {offers.map((o) => (
              <a key={o._id} href={o.link} className="offer-card" target={o.link.startsWith("http") ? "_blank" : undefined}
                rel={o.link.startsWith("http") ? "noopener noreferrer" : undefined}>
                <div className="offer-card-left">
                  {o.discount && <span className="offer-badge">{o.discount}</span>}
                  <img src={`/${o.image}`} alt={o.title} loading="lazy"
                    onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%231a1f29' width='120' height='120'/%3E%3Ctext fill='%238a93a6' x='50%25' y='50%25' text-anchor='middle' dy='.35em' font-size='32'%3E🎮%3C/text%3E%3C/svg%3E"; }} />
                </div>
                <div className="offer-card-right">
                  <div className="offer-card-top">
                    <h3 className="offer-title">{o.title}</h3>
                    <div className="offer-stars">
                      <span className="stars">{renderStars(o.rating)}</span>
                      <span className="rating-num">{o.rating}</span>
                    </div>
                  </div>
                  {o.sold && <div className="offer-sold"><span className="dot-green" />{o.sold}</div>}
                  {o.price && <div className="offer-price">{o.price}</div>}
                  <div className="offer-btn">{o.title}</div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {products === null && (
        <div className="home-loading">
          <div className="home-spinner" />
          <span>Loading games...</span>
        </div>
      )}

      {products && filtered.length === 0 && (
        <p className="home-empty">
          {search ? `No games matching "${search}"` : "No games available right now."}
        </p>
      )}

      {Object.keys(groups).map((category) => (
        <section key={category} className="home-section">
          <div className="sec-banner">
            <div className="sec-title">🚀 {category} <span className="sec-count">{groups[category].length}</span></div>
            <div className="sec-sub">Top games · Instant delivery · Best prices</div>
          </div>
          <div className="home-grid">
            {groups[category].map((p) => (
              <Link key={p.id} to={`/game/${p.id}`} className="game-card">
                <div className="game-card-img">
                  <img src={`/${p.image}`} alt={p.name} loading="lazy"
                    onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%231a1f29' width='120' height='120'/%3E%3Ctext fill='%238a93a6' x='50%25' y='50%25' text-anchor='middle' dy='.35em' font-size='32'%3E🎮%3C/text%3E%3C/svg%3E"; }} />
                </div>
                {p.company && <span className="game-card-badge">{p.company}</span>}
                {p.stock === "no" && <span className="game-card-oos">Out of Stock</span>}
                <div className="game-card-foot">
                  <div className="game-card-name">{p.name}</div>
                  <span className="game-card-buy">⚡ BUY NOW</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}